// Story 2.2 — query hook for GET /api/v1/admin/approved-creators.
//
// Powers the composer's Step 2 multi-select picker. Distinct from
// Story 1.23's public GET /api/v1/creators because the admin endpoint
// returns user_id (the FK target on portfolio_creators.creator_user_id)
// + richer fields (region, language, creator_approved_at) without an
// IP-based rate-limit ceiling.

import { useQuery } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../../lib/api-client";

export type ApprovedCreatorRow = {
  user_id: string;
  display_name: string | null;
  handle: string | null;
  region: string | null;
  language: string | null;
  creator_approved_at: string | null;
};

export type AdminApprovedCreatorsResponse = {
  data: ApprovedCreatorRow[];
};

async function fetchApprovedCreators(): Promise<AdminApprovedCreatorsResponse> {
  const response = await fetch("/api/v1/admin/approved-creators", {
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let message = `Approved creators fetch failed (HTTP ${response.status})`;
    try {
      const raw = await response.json();
      if (raw?.error?.message) message = raw.error.message;
    } catch {
      // body not JSON
    }
    throw new Error(message);
  }
  return (await response.json()) as AdminApprovedCreatorsResponse;
}

export function useAdminApprovedCreators() {
  return useQuery<AdminApprovedCreatorsResponse, Error>({
    queryKey: ["admin-approved-creators"],
    queryFn: fetchApprovedCreators,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: true,
  });
}
